// src/components/google_ads/GoogleAdsCampaignDetailPage.tsx
// Track E6 — campaign detail with metrics chart + ad groups.
//
// Reached via /m/mkt-google/campaigns/:subId. Shows the chart, status,
// bidding config + ad groups table. Ads + keywords are nested on the
// ad group row for scope.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Activity, ArrowLeft, Loader2, Pause, Play, Plus, RefreshCw, Send,
  Tag, Zap,
} from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import {
  ApiError,
  createAdGroup, getCampaign, getCampaignMetrics, listAdGroups,
  listAds, listKeywords, microsToCurrency, pauseCampaign, pushAdGroup,
  pushCampaign, resumeCampaign, triggerMetricsSync,
  type AdGroup, type Campaign, type MetricsSummary,
} from '@/lib/api_google_ads';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props { addNotification?: Notifier }

const WINDOWS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
];

export function GoogleAdsCampaignDetailPage({ addNotification }: Props) {
  const { subId } = useParams();
  const campaignId = Number(subId);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null);
  const [adGroups, setAdGroups] = useState<AdGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState<string | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cmp, m, groups] = await Promise.all([
        getCampaign(campaignId),
        getCampaignMetrics(campaignId, days).catch(() => null),
        listAdGroups({ campaign_id: campaignId, limit: 100 }),
      ]);
      setCampaign(cmp);
      setMetrics(m);
      setAdGroups(groups.items);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se pudo cargar',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setLoading(false);
    }
  }, [campaignId, days, addNotification]);

  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    if (!metrics) return [];
    return metrics.daily.map((d) => ({
      date: d.date.slice(5),   // MM-DD
      impressions: d.impressions,
      clicks: d.clicks,
      cost: d.cost_micros / 1_000_000,
      conversions: d.conversions,
    }));
  }, [metrics]);

  const onAction = async (op: 'push' | 'pause' | 'resume' | 'sync-metrics') => {
    if (!campaign) return;
    setBusy(op);
    try {
      if (op === 'push') await pushCampaign(campaign.id);
      if (op === 'pause') await pauseCampaign(campaign.id);
      if (op === 'resume') await resumeCampaign(campaign.id);
      if (op === 'sync-metrics') await triggerMetricsSync(days);
      addNotification?.({ type: 'success', title: 'Acción encolada' });
      await load();
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se pudo ejecutar',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy(null);
    }
  };

  if (loading && !campaign) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={28} className="animate-spin text-emerald-400" />
      </div>
    );
  }
  if (!campaign) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/m/mkt-google/campaigns" className="text-gray-400 hover:text-white">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h2 className="text-xl font-bold text-white">{campaign.name}</h2>
            <div className="text-xs text-gray-500">
              {campaign.campaign_type} · {campaign.bidding_strategy_type} ·
              {' '}{microsToCurrency(campaign.daily_budget_micros)} / día
              {campaign.google_campaign_id && <> · id <span className="font-mono">{campaign.google_campaign_id}</span></>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {(campaign.status === 'draft' || campaign.status === 'error') && (
            <button
              onClick={() => onAction('push')}
              disabled={!!busy}
              className="o-btn o-btn-sm o-btn-primary flex items-center gap-1"
            >
              {busy === 'push' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Push
            </button>
          )}
          {campaign.status === 'enabled' && (
            <button
              onClick={() => onAction('pause')}
              disabled={!!busy}
              className="o-btn o-btn-sm o-btn-ghost flex items-center gap-1"
            >
              {busy === 'pause' ? <Loader2 size={14} className="animate-spin" /> : <Pause size={14} />}
              Pausar
            </button>
          )}
          {campaign.status === 'paused' && (
            <button
              onClick={() => onAction('resume')}
              disabled={!!busy}
              className="o-btn o-btn-sm o-btn-ghost flex items-center gap-1"
            >
              {busy === 'resume' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              Activar
            </button>
          )}
        </div>
      </div>

      {/* Metrics chart */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Activity size={16} /> Métricas
          </h3>
          <div className="flex items-center gap-2">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setDays(w.days)}
                className={`o-btn o-btn-xs ${days === w.days ? 'o-btn-primary' : 'o-btn-ghost'}`}
              >
                {w.label}
              </button>
            ))}
            <button
              onClick={() => onAction('sync-metrics')}
              disabled={!!busy}
              className="o-btn o-btn-xs o-btn-ghost flex items-center gap-1"
              title="Forzar pull desde Google"
            >
              {busy === 'sync-metrics' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Sync
            </button>
          </div>
        </div>

        {metrics && metrics.totals.impressions > 0 ? (
          <>
            <div className="grid grid-cols-4 gap-3 mb-4">
              <Stat label="Impresiones" value={metrics.totals.impressions.toLocaleString('es-MX')} />
              <Stat label="Clicks" value={metrics.totals.clicks.toLocaleString('es-MX')} />
              <Stat label="Costo" value={microsToCurrency(metrics.totals.cost_micros)} />
              <Stat label="Conversiones" value={metrics.totals.conversions.toFixed(2)} />
            </div>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" stroke="#94a3b8" />
                  <YAxis yAxisId="left" stroke="#94a3b8" />
                  <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="impressions" stroke="#10b981" fillOpacity={0.15} fill="#10b981" />
                  <Area yAxisId="right" type="monotone" dataKey="clicks" stroke="#38bdf8" fillOpacity={0.2} fill="#38bdf8" />
                  <Area yAxisId="right" type="monotone" dataKey="conversions" stroke="#f59e0b" fillOpacity={0.2} fill="#f59e0b" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="py-10 text-center text-sm text-gray-500">
            Sin datos aún. Las métricas aparecen tras la primera ejecución del sync (beat cada hora a :15).
          </div>
        )}
      </div>

      {/* Ad groups */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Zap size={16} /> Ad groups ({adGroups.length})
          </h3>
          <button onClick={() => setShowCreateGroup(true)} className="o-btn o-btn-sm o-btn-primary flex items-center gap-1">
            <Plus size={14} /> Nuevo ad group
          </button>
        </div>
        {adGroups.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Sin ad groups. Crea uno para empezar a agregar ads y keywords.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400 uppercase text-xs">
              <tr>
                <th className="text-left px-4 py-2">Nombre</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-left px-4 py-2">CPC default</th>
                <th className="text-left px-4 py-2">Google ID</th>
                <th className="text-left px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {adGroups.map((g) => (
                <AdGroupRow
                  key={g.id}
                  group={g}
                  campaign={campaign}
                  onChange={load}
                  notify={addNotification}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreateGroup && (
        <CreateAdGroupDrawer
          campaignId={campaign.id}
          onClose={() => setShowCreateGroup(false)}
          onCreated={async () => { setShowCreateGroup(false); await load(); }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2">
      <div className="text-xs text-gray-400 uppercase">{label}</div>
      <div className="text-lg text-white font-semibold">{value}</div>
    </div>
  );
}

function AdGroupRow({
  group, campaign, onChange, notify,
}: {
  group: AdGroup; campaign: Campaign;
  onChange: () => Promise<void>; notify?: Notifier;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [children, setChildren] = useState<{ ads: any[]; keywords: any[] } | null>(null);

  const loadChildren = useCallback(async () => {
    try {
      const [ads, kws] = await Promise.all([
        listAds({ ad_group_id: group.id, limit: 50 }),
        listKeywords({ ad_group_id: group.id, limit: 100 }),
      ]);
      setChildren({ ads: ads.items, keywords: kws.items });
    } catch {
      /* handled via toast below */
    }
  }, [group.id]);

  useEffect(() => {
    if (open && !children) loadChildren();
  }, [open, children, loadChildren]);

  const push = async () => {
    setBusy(true);
    try {
      await pushAdGroup(group.id);
      notify?.({ type: 'success', title: 'Push encolado' });
      await onChange();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'Error',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className="hover:bg-white/[0.03]">
        <td className="px-4 py-2">
          <button
            onClick={() => setOpen((o) => !o)}
            className="text-white hover:text-emerald-400 text-left"
          >
            {group.name}
          </button>
          {group.error_message && (
            <div className="text-xs text-red-400 truncate max-w-xs">{group.error_message}</div>
          )}
        </td>
        <td className="px-4 py-2 text-gray-300">{group.status}</td>
        <td className="px-4 py-2 text-gray-300">{microsToCurrency(group.cpc_bid_micros)}</td>
        <td className="px-4 py-2 text-xs text-gray-500 font-mono">{group.google_ad_group_id || '—'}</td>
        <td className="px-4 py-2">
          {(group.status === 'draft' || group.status === 'error') && campaign.google_campaign_id && (
            <button
              onClick={push}
              disabled={busy}
              className="o-btn o-btn-xs o-btn-ghost flex items-center gap-1"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Push
            </button>
          )}
        </td>
      </tr>
      {open && children && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-gray-900/30 border-t border-gray-800">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-400 uppercase mb-1">
                  Ads ({children.ads.length})
                </div>
                {children.ads.length === 0 ? (
                  <div className="text-xs text-gray-500">Sin ads</div>
                ) : (
                  <ul className="space-y-1">
                    {children.ads.map((a) => {
                      const firstHeadline = Array.isArray(a.headlines) && a.headlines[0];
                      const text = firstHeadline && typeof firstHeadline === 'object'
                        ? firstHeadline.text
                        : String(firstHeadline || '—');
                      return (
                        <li key={a.id} className="text-sm text-gray-200 flex justify-between">
                          <span className="truncate">{text}</span>
                          <span className="text-xs text-gray-500">{a.status}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase mb-1">
                  Keywords ({children.keywords.length})
                </div>
                {children.keywords.length === 0 ? (
                  <div className="text-xs text-gray-500">Sin keywords</div>
                ) : (
                  <ul className="space-y-1">
                    {children.keywords.slice(0, 10).map((k) => (
                      <li key={k.id} className="text-sm text-gray-200 flex justify-between">
                        <span className="truncate">
                          <Tag size={10} className="inline mr-1 opacity-60" />
                          <span className="font-mono">[{k.match_type}]</span> {k.text}
                        </span>
                        <span className="text-xs text-gray-500">{k.status}</span>
                      </li>
                    ))}
                    {children.keywords.length > 10 && (
                      <li className="text-xs text-gray-500">+ {children.keywords.length - 10} más</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function CreateAdGroupDrawer({
  campaignId, onClose, onCreated, notify,
}: {
  campaignId: number;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const [form, setForm] = useState({
    name: '', cpc_bid_micros: 1_000_000,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createAdGroup({
        campaign_id: campaignId,
        name: form.name,
        cpc_bid_micros: form.cpc_bid_micros || null,
      });
      notify?.({ type: 'success', title: 'Ad group creado' });
      await onCreated();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'No se pudo crear',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Nuevo ad group</h3>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Nombre</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="grupo-1"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">CPC default (micros)</label>
              <input
                type="number"
                value={form.cpc_bid_micros}
                onChange={(e) => setForm((f) => ({ ...f, cpc_bid_micros: Number(e.target.value) }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                {microsToCurrency(form.cpc_bid_micros)} por click aprox.
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-gray-700">
              <button type="button" onClick={onClose} className="o-btn o-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="o-btn o-btn-primary flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Crear
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default GoogleAdsCampaignDetailPage;
